import type { SkillExitSlipProgress, SkillExitSlipTicket } from '../../models/ExitSlip';
import { EXIT_SLIP_PASSING_RATIO } from './exitSlipOutcome';

// One row of the badge's hover panel: how many tickets fell in this group, and the group's score.
export interface ExitSlipTicketGroup {
  count: number;
  // Whole percent (the design shows "80%", "30%"), averaged across the group's tickets. 0 when the
  // group is empty, which the panel does not render anyway.
  scorePercent: number;
}

// What the badge and its hover panel render, derived from one skill's SkillExitSlipProgress.
export interface SkillExitSlipBadgeSummary {
  // The number on the badge's chip: every ticket for this skill the student has finished, passed or
  // not. Same rule as the unit header's badge count, except scoped to this one skill.
  completedCount: number;
  // Finished at or above the passing bar.
  passed: ExitSlipTicketGroup;
  // Finished below it. The design lists these as "Taken", matching the collapsed banner's wording
  // for the same outcome — so a ticket is in exactly one of the two groups, never both.
  taken: ExitSlipTicketGroup;
  isOnFire: boolean;
  // The student has sat at least one of this skill's tickets, passed every one of them, and does not
  // have the skill yet. The panel swaps its heading for "Seems ready to try challenge" and offers a
  // way through to the skill.
  //
  // How many tickets are left to sit does not come into it: the rule is about the ones the student
  // actually sat, so a skill with a single ticket sat and passed qualifies. What does come into it is
  // whether the skill is already theirs — a skill they have earned has no challenge left to invite
  // them to, which is the same reason isOnFire goes out at that point.
  isReadyForChallenge: boolean;
}

const isTicketPassed = (ticket: SkillExitSlipTicket): boolean => ticket.scoreRatio >= EXIT_SLIP_PASSING_RATIO;

const toGroup = (tickets: SkillExitSlipTicket[]): ExitSlipTicketGroup => ({
  count: tickets.length,
  scorePercent:
    tickets.length === 0
      ? 0
      : Math.round((tickets.reduce((total, ticket) => total + ticket.scoreRatio, 0) / tickets.length) * 100),
});

/**
 * Whether this skill's exit tickets have put the student "on fire" — the state that earns the fire
 * booster above the badge.
 *
 * Both halves of the rule (ON FIRE FR-1) have to hold:
 *  - the student passed the exit ticket of the *final* lesson aligned to the skill, which is what
 *    `finalLessonPassed` carries (see finalAlignedLessonPassed for how that lesson is picked), and
 *  - the skill is not already passed or inferred for them.
 *
 * The second half is why this takes an argument the report cannot supply: whether a skill is already
 * earned lives in the student's skill state, not in their exit-ticket history. The badge is a nudge
 * towards a skill within reach, so a skill already in hand has nothing to nudge towards.
 *
 * Note what is *not* here any more: the old rule counted the share of a skill's tickets the student
 * had sat, whatever they scored. It has been replaced outright — sitting tickets no longer earns the
 * fire, and neither does passing any ticket other than the last one (FR-3).
 */
export const isSkillOnFire = ({ finalLessonPassed }: SkillExitSlipProgress, skillAlreadyEarned: boolean): boolean =>
  finalLessonPassed && !skillAlreadyEarned;

/**
 * Reduces one skill's exit-ticket history to what the card's badge shows: the completed count on its
 * chip, the passed/taken breakdown in its hover panel, and whether the fire booster applies.
 *
 * `skillAlreadyEarned` is the card's own reading of the skill's state (isSkillCompleted). Both of the
 * panel's invitations — the fire and the challenge — are held back by it: a skill the student already
 * has is not somewhere to be nudged towards.
 */
export const summarizeSkillExitSlips = (
  progress: SkillExitSlipProgress,
  skillAlreadyEarned: boolean,
): SkillExitSlipBadgeSummary => {
  const passed = progress.completedTickets.filter(isTicketPassed);
  const taken = progress.completedTickets.filter(ticket => !isTicketPassed(ticket));

  return {
    completedCount: progress.completedTickets.length,
    passed: toGroup(passed),
    taken: toGroup(taken),
    isOnFire: isSkillOnFire(progress, skillAlreadyEarned),
    isReadyForChallenge: passed.length > 0 && taken.length === 0 && !skillAlreadyEarned,
  };
};
